import React, { useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Logo1 from "../images/cahoot_logo.png";
import Logo2 from "../images/client-01.png";
import Logo3 from "../images/client-02.png";
import Logo4 from "../images/client-03.png";
import Logo5 from "../images/client-04.png";
import Logo6 from "../images/client-05.png";
import Logo7 from "../images/client-06.png";
import Logo8 from "../images/client-07.png";
import MatchingWorkflowGraphic from "../images/graphics/matching-workflow.svg";
import PageHero from "../components/pageHero";
import { buildApiUrl } from "../utils/api";
import { contactMethodOptions, productCategoryOptions, serviceNeedOptions, storageTypeOptions, timelineOptions } from "../utils/marketplaceOptions";

const logos = [Logo1, Logo2, Logo3, Logo4, Logo5, Logo6, Logo7, Logo8];

const initialForm = {
  name: "",
  email: "",
  contactNumber: "",
  firmName: "",
  location: "",
  firmWebsite: "",
  salesChannel: "",
  totalSku: "",
  avgMonthlyOrders: "",
  productType: "",
  serviceNeeds: "",
  fulfillmentRegions: "",
  storageType: "",
  temperatureRequirement: "",
  preferredGoLiveDate: "",
  preferredContactMethod: "Email",
  summary: ""
};

function BookingMarketplace() {
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState(null);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ text: "Submitting your requirement...", type: "success" });

    try {
      await axios.post(buildApiUrl("/api/finders"), formData);
      setFormData(initialForm);
      setStatus({ text: "Requirement submitted. Your CS team can now shortlist providers and prepare quote options.", type: "success" });
    } catch (error) {
      console.error("Error submitting finder form", error);
      setStatus({ text: "We could not submit the requirement right now. Please try again.", type: "error" });
    }
  };

  return (
    <>
      <Helmet>
        <title>Request a 3PL Match | 3PL Search</title>
      </Helmet>

      <div className="booking-page">
        <PageHero
          eyebrow="Guided buyer brief"
          title="Tell us what you need and let CS build your shortlist."
          description="Use this single primary buyer form when you want help comparing providers, shaping fit, and getting the quoting process started quickly."
          secondaryAction={{ to: "/finder", label: "Browse directory first" }}
          image={MatchingWorkflowGraphic}
          imageAlt="Buyer brief and matching infographic"
          imageLight
          compact
        >
          <div className="logo-marquee compact-logos">
            <div className="logo-track">
              {logos.concat(logos).map((logo, index) => (
                <div className="logo-item modern no-border" key={`${logo}-${index}`}>
                  <img src={logo} alt={`Client logo ${index + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </PageHero>

        <section className="content-section pt-0">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <form className="lead-form-card" onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6"><label>Full name</label><input name="name" value={formData.name} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Company name</label><input name="firmName" value={formData.firmName} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Work email</label><input type="email" name="email" value={formData.email} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Phone / WhatsApp</label><input name="contactNumber" value={formData.contactNumber} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Current market / base region</label><input name="location" value={formData.location} onChange={handleChange} placeholder="Dubai, New Jersey, Singapore..." required /></div>
                    <div className="col-md-6"><label>Company website</label><input name="firmWebsite" value={formData.firmWebsite} onChange={handleChange} /></div>
                    <div className="col-md-6"><label>Sales channels</label><input name="salesChannel" value={formData.salesChannel} onChange={handleChange} placeholder="Shopify, Amazon, B2B wholesale" required /></div>
                    <div className="col-md-6"><label>Product category</label><select name="productType" value={formData.productType} onChange={handleChange} required><option value="">Select category</option>{productCategoryOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                    <div className="col-md-6"><label>SKU count</label><input type="number" name="totalSku" value={formData.totalSku} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Monthly orders</label><input type="number" name="avgMonthlyOrders" value={formData.avgMonthlyOrders} onChange={handleChange} required /></div>
                    <div className="col-md-6"><label>Primary service need</label><select name="serviceNeeds" value={formData.serviceNeeds} onChange={handleChange}><option value="">Select service</option>{serviceNeedOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                    <div className="col-md-6"><label>Storage type</label><select name="storageType" value={formData.storageType} onChange={handleChange}><option value="">Select storage</option>{storageTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                    <div className="col-md-6"><label>Regions to fulfill</label><input name="fulfillmentRegions" value={formData.fulfillmentRegions} onChange={handleChange} placeholder="US East, Europe, GCC" /></div>
                    <div className="col-md-6"><label>Temperature requirement</label><input name="temperatureRequirement" value={formData.temperatureRequirement} onChange={handleChange} placeholder="Ambient / chilled / frozen" /></div>
                    <div className="col-md-6"><label>Preferred contact</label><select name="preferredContactMethod" value={formData.preferredContactMethod} onChange={handleChange}>{contactMethodOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                    <div className="col-md-6"><label>Go-live timeline</label><select name="preferredGoLiveDate" value={formData.preferredGoLiveDate} onChange={handleChange}><option value="">Select timeline</option>{timelineOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                    <div className="col-12"><label>Requirement summary</label><textarea rows="4" name="summary" value={formData.summary} onChange={handleChange} placeholder="Tell us about packaging, inbound frequency, returns, marketplaces, compliance, or any must-have capabilities." required /></div>
                  </div>
                  <div className="lead-form-actions">
                    <button className="site-primary-cta" type="submit">Submit requirement</button>
                    <Link className="site-secondary-cta" to="/finder">Browse directory instead</Link>
                  </div>
                  {status ? <p className={`form-status ${status.type}`}>{status.text}</p> : null}
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default BookingMarketplace;
